/**
 * Orderings for field values. A type's `sort` compares two raw values of a field
 * the way `Array.prototype.sort` wants; a collection calls it as `field.sort(a, b,
 * field)` and applies the direction itself. A type with no `sort` is not sortable.
 */

/** Negative, zero or positive, like an `Array.prototype.sort` comparator. `field`
 *  carries what an ordering reads off the field (a select's `values`). */
export type Compare = (a: unknown, b: unknown, field: { values?: readonly unknown[] }) => number

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" })

/** An entity's display name, else the value itself. */
export function entityName(value: unknown): unknown {
  const name = (value as { name?: unknown } | null)?.name
  return typeof value === "object" && name ? name : value
}

/** The present elements of a value: a scalar is one, an array is its non-null items. */
const elements = (value: unknown): unknown[] =>
  (Array.isArray(value) ? value : [value]).filter((item) => item != null).map(entityName)

/** An ordering over raw field values from one over two present values. Nulls sort
 *  last. A multi-value field compares its elements sorted lowest first, in order, so
 *  the lowest element decides and the next breaks a tie; an entity compares by its name. */
export function ordered(
  compare: (a: unknown, b: unknown, field: { values?: readonly unknown[] }) => number
): Compare {
  return (a, b, field) => {
    const x = elements(a)
    const y = elements(b)
    if (x.length === 0 && y.length === 0) return 0
    if (x.length === 0) return 1
    if (y.length === 0) return -1
    const byField = (p: unknown, q: unknown) => compare(p, q, field)
    if (x.length > 1) x.sort(byField)
    if (y.length > 1) y.sort(byField)
    for (let i = 0; i < Math.min(x.length, y.length); i++) {
      const result = compare(x[i], y[i], field)
      if (result !== 0) return result
    }
    return x.length - y.length
  }
}

/** Text in the user's locale, digit runs as numbers, ignoring case. */
export const compareText = (a: unknown, b: unknown): number =>
  collator.compare(String(a), String(b))

const byValue = <T>(a: T, b: T): number => (a < b ? -1 : a > b ? 1 : 0)

/** Text in the user's locale, with digit runs compared as numbers ("2" before "10"). */
export const text: Compare = ordered(compareText)

/** Numbers by value; a value that is not a number falls back to text. */
export const numeric: Compare = ordered((a, b) => {
  const x = Number(a)
  const y = Number(b)
  return Number.isNaN(x) || Number.isNaN(y) ? compareText(a, b) : byValue(x, y)
})

/** Dates and ISO timestamps by instant; a value that is not a date falls back to text. */
export const timestamp: Compare = ordered((a, b) => {
  const x = new Date(a as string | number | Date).getTime()
  const y = new Date(b as string | number | Date).getTime()
  return Number.isNaN(x) || Number.isNaN(y) ? compareText(a, b) : byValue(x, y)
})

/** True before false. */
export const boolean: Compare = ordered((a, b) => (a === b ? 0 : a ? -1 : 1))

/** A select's options in the order the field defines them; a value the field does
 *  not list follows the listed ones, in text order. */
export const option: Compare = ordered((a, b, field) => {
  const values = field.values ?? []
  const x = values.indexOf(a)
  const y = values.indexOf(b)
  if (x < 0 && y < 0) return compareText(a, b)
  if (x < 0) return 1
  if (y < 0) return -1
  return x - y
})

/** The `start_at` (ms) of each time row in a row or rows, earliest first. */
function startsOf(times: unknown): number[] {
  const starts: number[] = []
  for (const row of Array.isArray(times) ? times : times == null ? [] : [times]) {
    const startAt = (row as { start_at?: string | null } | null)?.start_at
    if (!startAt) continue
    const ms = new Date(startAt).getTime()
    if (Number.isFinite(ms)) starts.push(ms)
  }
  return starts.sort((a, b) => a - b)
}

/** Time rows by when each occurs: the earliest start decides and the next breaks a tie.
 *  A record with no timed row sorts last. */
export const occurrence: Compare = (a, b) => {
  const x = startsOf(a)
  const y = startsOf(b)
  if (x.length === 0 && y.length === 0) return 0
  if (x.length === 0) return 1
  if (y.length === 0) return -1
  for (let i = 0; i < Math.min(x.length, y.length); i++) {
    if (x[i] !== y[i]) return x[i]! - y[i]!
  }
  return x.length - y.length
}
